import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { TabsList } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

type ScrollableTabsListProps = React.ComponentPropsWithoutRef<typeof TabsList>;

const ARROW_WIDTH = 'w-6';

const ScrollableTabsList = React.forwardRef<React.ElementRef<typeof TabsList>, ScrollableTabsListProps>(
  ({ className, children, ...props }, ref) => {
    const scrollRef = React.useRef<HTMLDivElement>(null);
    const [hasOverflow, setHasOverflow] = React.useState(false);
    const [canScrollLeft, setCanScrollLeft] = React.useState(false);
    const [canScrollRight, setCanScrollRight] = React.useState(false);

    const updateScrollState = React.useCallback(() => {
      const element = scrollRef.current;
      if (!element) return;

      const { scrollLeft, scrollWidth, clientWidth } = element;
      const overflow = scrollWidth > clientWidth + 1;

      setHasOverflow(overflow);
      setCanScrollLeft(scrollLeft > 1);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
    }, []);

    React.useEffect(() => {
      const element = scrollRef.current;
      if (!element) return;

      element.addEventListener('scroll', updateScrollState, { passive: true });
      const resizeObserver = new ResizeObserver(updateScrollState);
      resizeObserver.observe(element);

      return () => {
        element.removeEventListener('scroll', updateScrollState);
        resizeObserver.disconnect();
      };
    }, [updateScrollState]);

    React.useEffect(() => {
      updateScrollState();
    }, [children, updateScrollState]);

    const scrollTabs = (direction: 'left' | 'right') => {
      const element = scrollRef.current;
      if (!element) return;

      element.scrollBy({
        left: direction === 'left' ? -element.clientWidth * 0.6 : element.clientWidth * 0.6,
        behavior: 'smooth',
      });
    };

    const setMergedRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        scrollRef.current = node;

        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref]
    );

    return (
      <div className="flex w-full items-stretch">
        {hasOverflow && (
          <button
            type="button"
            aria-label="Scroll tabs left"
            disabled={!canScrollLeft}
            className={cn(
              'flex shrink-0 items-center justify-center bg-white',
              ARROW_WIDTH,
              !canScrollLeft && 'cursor-default opacity-30'
            )}
            onClick={() => scrollTabs('left')}
          >
            <ChevronLeft className="h-4 w-4 shrink-0 text-[#676767]" />
          </button>
        )}

        <TabsList
          ref={setMergedRef}
          className={cn('min-w-0 flex-1 overflow-x-auto overflow-y-hidden whitespace-nowrap scrollbar-hide', className)}
          {...props}
        >
          {children}
        </TabsList>

        {hasOverflow && (
          <button
            type="button"
            aria-label="Scroll tabs right"
            disabled={!canScrollRight}
            className={cn(
              'flex shrink-0 items-center justify-center bg-white',
              ARROW_WIDTH,
              !canScrollRight && 'cursor-default opacity-30'
            )}
            onClick={() => scrollTabs('right')}
          >
            <ChevronRight className="h-4 w-4 shrink-0 text-[#676767]" />
          </button>
        )}
      </div>
    );
  }
);

ScrollableTabsList.displayName = 'ScrollableTabsList';

export { ScrollableTabsList };
